// Google Picker API utility for selecting Google Sheets with drive.file scope

class GooglePicker {
  constructor() {
    this.pickerApiLoaded = false;
    this.oauthToken = null;
    this.callback = null;
    /** @type {null | (() => void)} */
    this.cleanupLayoutBump = null;
  }

  // Load the Google Picker API
  loadPickerAPI(apiKey, callback) {
    if (this.pickerApiLoaded) {
      callback();
      return;
    }

    gapi.load("picker", () => {
      this.pickerApiLoaded = true;
      callback();
    });
  }

  // Create and show the picker
  createPicker(oauthToken, callback) {
    this.oauthToken = oauthToken;
    this.callback = callback;

    if (!this.pickerApiLoaded) {
      console.error("Picker API not loaded");
      return;
    }

    const picker = new google.picker.PickerBuilder()
      .addView(
        new google.picker.DocsView()
          .setIncludeFolders(false)
          .setSelectFolderEnabled(false)
          .setMimeTypes("application/vnd.google-apps.spreadsheet")
          .setMode(google.picker.DocsViewMode.LIST)
      )
      .setOAuthToken(oauthToken)
      .setDeveloperKey(import.meta.env.VITE_GOOGLE_API_KEY || "")
      .setCallback(this.pickerCallback.bind(this))
      .setTitle("Select a Google Sheet")
      .setSelectableMimeTypes("application/vnd.google-apps.spreadsheet")
      .enableFeature(google.picker.Feature.NAV_HIDDEN)
      .enableFeature(google.picker.Feature.MULTISELECT_ENABLED, false)
      .build();

    picker.setVisible(true);
    this.scheduleGoogleEmbeddedUiLayoutBump();
  }

  /**
   * Google's picker / consent iframes often measure viewport on attach; locked scroll
   * (no scrollbar), late DOM injection, or first paint quirks leave wrong dimensions
   * until a real resize. Mirror that with reflow + staggered resize events.
   */
  scheduleGoogleEmbeddedUiLayoutBump() {
    if (typeof this.cleanupLayoutBump === "function") {
      this.cleanupLayoutBump();
      this.cleanupLayoutBump = null;
    }

    const bumpViewportLayout = () => {
      void document.documentElement.offsetHeight;
      window.dispatchEvent(new Event("resize"));
      window.dispatchEvent(new Event("scroll"));
      document.dispatchEvent(new Event("scroll"));
      const vv = window.visualViewport;
      if (vv) {
        vv.dispatchEvent(new Event("resize"));
      }
    };

    /** @param {HTMLIFrameElement} iframe */
    const onGoogleIframeSeen = (iframe) => {
      bumpViewportLayout();
      requestAnimationFrame(() => requestAnimationFrame(bumpViewportLayout));
      iframe.addEventListener(
        "load",
        () => {
          bumpViewportLayout();
          requestAnimationFrame(() => requestAnimationFrame(bumpViewportLayout));
          setTimeout(bumpViewportLayout, 120);
          setTimeout(bumpViewportLayout, 400);
        },
        { once: true },
      );
    };

    /** @param {Element} el */
    const looksLikeGoogleIframe = (el) => {
      if (el.tagName !== "IFRAME") return false;
      const src = el.getAttribute("src") || "";
      return /google\.com|googleusercontent\.com|gstatic\.com|docs\.google/.test(src);
    };

    /** @type {number[]} */
    const timeoutIds = [];
    timeoutIds.push(
      ...[0, 50, 160, 360, 600, 900, 1200].map((ms) =>
        setTimeout(() => bumpViewportLayout(), ms),
      ),
    );

    requestAnimationFrame(() => {
      requestAnimationFrame(bumpViewportLayout);
    });

    const observer =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(() => {
            const iframes = document.documentElement.querySelectorAll("iframe");
            for (const frame of iframes) {
              if (looksLikeGoogleIframe(frame) && !frame.dataset.pagetestPickerBump) {
                frame.dataset.pagetestPickerBump = "1";
                onGoogleIframeSeen(frame);
              }
            }
          })
        : null;

    if (observer) {
      observer.observe(document.documentElement, {
        attributes: false,
        childList: true,
        subtree: true,
      });

      for (const frame of document.querySelectorAll("iframe")) {
        if (looksLikeGoogleIframe(frame) && !frame.dataset.pagetestPickerBump) {
          frame.dataset.pagetestPickerBump = "1";
          onGoogleIframeSeen(frame);
        }
      }
    }

    const observerStopTimer = observer
      ? setTimeout(() => {
          observer.disconnect();
        }, 18_000)
      : undefined;

    this.cleanupLayoutBump = () => {
      if (observer) observer.disconnect();
      if (observerStopTimer != null) clearTimeout(observerStopTimer);
      timeoutIds.forEach((id) => clearTimeout(id));
    };
  }

  // Handle picker callback
  pickerCallback(data) {
    if (data.action === google.picker.Action.PICKED) {
      const doc = data.docs[0];
      const fileId = doc.id;
      const fileName = doc.name;

      if (this.callback) {
        this.callback({
          success: true,
          fileId: fileId,
          fileName: fileName,
          url: doc.url,
        });
      }
    } else if (data.action === google.picker.Action.CANCEL) {
      if (this.callback) {
        this.callback({
          success: false,
          error: "User cancelled selection",
        });
      }
    }
  }
}

// Export singleton instance
const googlePicker = new GooglePicker();
export default googlePicker;
